import { Delete as DeleteIcon, DragIndicator, TextFields as TextFieldsIcon } from '@mui/icons-material';
import { Box, FormHelperText, IconButton, InputAdornment, MenuItem, Select, Switch, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType, EAttributeUnit, EAttributeUnitFamily, INumberValidation, ISelectionValidation, ITextValidation } from '../../@types/AttributeTypes';
import { EUnitType } from '../../@types/Enums';
import AttributeModel from '../../models/standard/AttributeModel';
import { usePlan } from '../../provider/PlanProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import {
  attributeUnitDisplayMap,
  convertAttributeValue,
  getCorrespondingUnit,
  IMPERIAL_TO_METRIC,
  METRIC_TO_IMPERIAL,
  unitFamilyToUnitRecord
} from '../../utils/attributes/attributeUtils';
import { DefaultAttributeId } from '../../utils/attributes/defaultObjectsAndAttributes';
import { coerceSelectionValueForStorage, getDefaultSelectionValue, getLocalizedSelectionLabel, getSelectionDisplayText, getSelectionOptions, getSelectionValueForSelect, SelectionValue, serializeSelectionValue } from '../../utils/attributes/selectionAttributeUtils';

// Add Scaling Factors
const METRIC_SCALE_FACTORS: Partial<Record<EAttributeUnit, number>> = {
  [EAttributeUnit.MM]: 0.001,
  [EAttributeUnit.CM]: 0.01,
  [EAttributeUnit.DM]: 0.1,
  [EAttributeUnit.M]: 1,
  [EAttributeUnit.KM]: 1000,
  [EAttributeUnit.MM_2]: 1e-6,
  [EAttributeUnit.CM_2]: 1e-4,
  [EAttributeUnit.DM_2]: 1e-2,
  [EAttributeUnit.M_2]: 1,
  [EAttributeUnit.KM_2]: 1e6,
  [EAttributeUnit.MM_3]: 1e-9,
  [EAttributeUnit.CM_3]: 1e-6,
  [EAttributeUnit.DM_3]: 1e-3,
  [EAttributeUnit.M_3]: 1,
  [EAttributeUnit.L]: 1,
};

interface AttributeListProps {
  attributes: AttributeModel[];
  attributeValues: Record<number, string>;
  showInputFields: Record<number, boolean>;
  onAttributeValueChange: (attrId: number, value: string) => void;
  onAttributeUnitChange?: (attrId: number, unit: string) => void;
  onDeleteAttribute: (attrId: number) => void;
  onToggleInputField: (attrId: number) => void;
  onReorderAttributes: (newAttributes: AttributeModel[]) => void;
  showDragHandle?: boolean;
}

const AttributeList: React.FC<AttributeListProps> = ({
  attributes,
  attributeValues,
  showInputFields,
  onAttributeValueChange,
  onAttributeUnitChange,
  onDeleteAttribute,
  onToggleInputField,
  onReorderAttributes,
  showDragHandle = true,
}) => {
  const intl = useIntl();
  const { unitType } = useUnitContext();
  const { planModel } = usePlan();

  const currentLocale = intl.locale.split('-')[0];
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const isRegionUS = regionCode === 'US';
  const defaultsRegionKey = isRegionUS ? 'US' : 'DE';
  const isImperialDisplay = unitType === EUnitType.IMPERIAL;

  const [editingId, setEditingId] = useState<number | null>(null);
  const [localValue, setLocalValue] = useState<string>('');
  const [errors, setErrors] = useState<Record<number, string | null>>({});

  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [textFieldVisible, setTextFieldVisible] = useState(showInputFields);

  // STRICT SCROLL BLOCKER ---
  useEffect(() => {
    const activeInput = editingId !== null ? inputRefs.current[editingId] : null;
    if (activeInput) {
      const preventScroll = (e: WheelEvent) => {
        e.preventDefault();
      };
      activeInput.addEventListener('wheel', preventScroll, { passive: false });
      return () => activeInput.removeEventListener('wheel', preventScroll);
    }
  }, [editingId]);

  // INITIALIZATION ---
  useEffect(() => {
    attributes.forEach((attr) => {
      const isUninitialized = attributeValues[attr.id] === undefined || attributeValues[attr.id] === null;
      const defaultObj = attr.defaults?.[defaultsRegionKey] || attr.defaults?.GLOBAL;

      // check if the model itself already has the value before overwriting it!
      const modelHasValue = attr.value !== undefined && attr.value !== null && String(attr.value) !== '';

      if (attr.type === EAttributeType.BOOL && (isUninitialized || attributeValues[attr.id] === '')) {
        let valToSet = 'false'; 
        if (modelHasValue) {
           valToSet = String(attr.value);
        } else if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
           valToSet = String(defaultObj.value);
        } else {
           // Legacy fallback for booleans without defaults
           valToSet = 'true'; 
        }
        onAttributeValueChange(attr.id, valToSet);
      }

      if (attr.type === EAttributeType.SELECTION && isUninitialized && !modelHasValue) {
        const validationObj = attr.validation?.[defaultsRegionKey] || attr.validation?.GLOBAL || {};
        const validValues = getSelectionOptions((validationObj as ISelectionValidation)?.values as SelectionValue[] | undefined);
        const defaultValue = getDefaultSelectionValue(defaultObj?.value, validValues);
      
        if (defaultValue !== null) {
          onAttributeValueChange(attr.id, serializeSelectionValue(defaultValue));
        }
      }

      if (attr.type === EAttributeType.TEXT && isUninitialized && !modelHasValue) {
        if (defaultObj?.value) {
          onAttributeValueChange(attr.id, String(defaultObj.value));
        }
      }

      if (attr.type === EAttributeType.NUMBER) {
        if (isUninitialized && !modelHasValue) {
          if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
            onAttributeValueChange(attr.id, String(defaultObj.value));
          }
        }
        if (!attr.unit && onAttributeUnitChange) {
          const validationObj = attr.validation?.[defaultsRegionKey] || attr.validation?.GLOBAL || {};
          const targetUnit = (validationObj as INumberValidation).unit || defaultObj?.unit;
          if (targetUnit) {
            onAttributeUnitChange(attr.id, targetUnit);
          }
        }
      }
    });
  }, [attributes, defaultsRegionKey, isRegionUS]);

  // --- HANDLERS ---
  const getLocalizedLabel = (attr: AttributeModel): string => {
    if (attr.localization) {
      return attr.localization[currentLocale] || attr.localization.en || attr.key;
    }
    return attr.key;
  };
  
  const handleInputFocus = (id: number, currentDisplayValue: string) => {
    setEditingId(id);
    setLocalValue(currentDisplayValue);
    setErrors((prev) => ({ ...prev, [id]: null }));
  };

  const handleInputBlur = (attr: AttributeModel, displayUnit: EAttributeUnit, storageUnit: EAttributeUnit, isNativeMode: boolean, validation: INumberValidation) => {
    if (attr.type !== EAttributeType.NUMBER) {
      setEditingId(null);
      setLocalValue('');
      setErrors((prev) => ({ ...prev, [attr.id]: null }));
      return;
    }

    const valStr = localValue.replace(',', '.');
    let num = parseFloat(valStr);
    let clamped = false;

    if (!isNaN(num)) {
      if (validation.min !== undefined && validation.min !== null && num < validation.min) {
        num = validation.min;
        clamped = true;
      } else if (validation.max !== undefined && validation.max !== null && num > validation.max) {
        num = validation.max;
        clamped = true;
      }
    }

    if (clamped) {
      const precision = validation.precision ?? 3;
      const factor = Math.pow(10, precision);
      num = Math.round(num * factor) / factor;
      const newStr = String(num);
      saveNumberToParent(attr, newStr, displayUnit, storageUnit, isNativeMode, validation);
    }

    setEditingId(null);
    setLocalValue('');
    setErrors((prev) => ({ ...prev, [attr.id]: null }));
  };

  const saveNumberToParent = (
    attr: AttributeModel,
    valStr: string,
    displayUnit: EAttributeUnit,
    storageUnit: EAttributeUnit,
    isNativeMode: boolean,
    validation: INumberValidation,
  ) => {
    const num = parseFloat(valStr);
    const precision = validation.precision ?? 3;
    let valueToSave = valStr;

    if (!isNativeMode && displayUnit && storageUnit && displayUnit !== storageUnit) {
      if (!isNaN(num)) {
        const roundFactor = Math.pow(10, precision);
        let converted = num;

        if (isImperialDisplay) {
          converted = convertAttributeValue(num, displayUnit, false);
          const scale = METRIC_SCALE_FACTORS[storageUnit] || 1;
          converted = converted / scale;
        } else {
          converted = convertAttributeValue(num, displayUnit, true);
        }

        valueToSave = String(Math.round(converted * roundFactor) / roundFactor);
      }
    }
    onAttributeValueChange(attr.id, valueToSave);
  };

  const handleNumberChange = (
    attr: AttributeModel,
    raw: string,
    displayUnit: EAttributeUnit,
    storageUnit: EAttributeUnit,
    isNativeMode: boolean,
    validation: INumberValidation,
  ) => {
    const val = raw.replace(',', '.');
    const allowNegative = validation.min !== undefined && validation.min < 0;

    if (val === '') {
      setLocalValue('');
      setErrors((prev) => ({ ...prev, [attr.id]: null }));
      onAttributeValueChange(attr.id, '');
      return;
    }
    if (val === '-' || val === '.') {
      if (val === '-' && !allowNegative) return;
      setLocalValue(val);
      return;
    }

    const maxPrecision = validation.precision ?? 3;
    const negativePattern = allowNegative ? '-?' : '';
    const regex = new RegExp(`^${negativePattern}(0|[1-9]\\d*)(\\.\\d{0,${maxPrecision}})?$`);

    if (!val.endsWith('.') && !regex.test(val)) return;

    setLocalValue(val);

    const num = parseFloat(val);
    let errorMsg = null;
    if (!isNaN(num)) {
      if (validation.min !== undefined && validation.min !== null && num < validation.min) {
        errorMsg = `Min: ${validation.min}`;
      } else if (validation.max !== undefined && validation.max !== null && num > validation.max) {
        errorMsg = `Max: ${validation.max}`;
      }
    }

    if (errorMsg) {
      setErrors((prev) => ({ ...prev, [attr.id]: errorMsg }));
      return;
    }

    setErrors((prev) => ({ ...prev, [attr.id]: null }));
    saveNumberToParent(attr, val, displayUnit, storageUnit, isNativeMode, validation);
  };

  const handleTextChange = (attrId: number, value: string, validation: ITextValidation) => {
    setLocalValue(value);

    const maxLength = validation.maxLength ?? 150;

    let errorMsg = null;
    if (value.length > maxLength) return;

    if (validation.minLength && value.length > 0 && value.length < validation.minLength) {
      errorMsg = `Min length: ${validation.minLength}`;
    }
    if (validation.pattern) {
      try {
        const regex = new RegExp(validation.pattern);
        if (value !== '' && !regex.test(value)) {
          errorMsg = 'Invalid format';
        }
      } catch (e) {
        console.error('Invalid Regex', e);
      }
    }
    setErrors((prev) => ({ ...prev, [attrId]: errorMsg }));

    if (!errorMsg) {
      onAttributeValueChange(attrId, value);
    }
  };

  const handleStepChange = (
    delta: number,
    attr: AttributeModel,
    currentVal: string,
    displayUnit: EAttributeUnit,
    storageUnit: EAttributeUnit,
    isNativeMode: boolean,
    validation: INumberValidation,
  ) => {
    const num = parseFloat(currentVal);
    const startVal = isNaN(num) ? validation.min ?? 0 : num;

    const step = validation.step ?? 1;
    const maxPrecision = validation.precision ?? 3;
    const newValue = startVal + delta * step;

    const roundFactor = Math.pow(10, maxPrecision);
    const roundedValue = Math.round(newValue * roundFactor) / roundFactor;

    let finalVal = roundedValue;
    if (validation.min !== undefined && finalVal < validation.min) finalVal = validation.min;
    if (validation.max !== undefined && finalVal > validation.max) finalVal = validation.max;

    setLocalValue(String(finalVal));
    saveNumberToParent(attr, String(finalVal), displayUnit, storageUnit, isNativeMode, validation);
  };

  const handleKeyDown = (e: React.KeyboardEvent, ...args: any[]) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleStepChange(1, ...(args as [any, any, any, any, any, any]));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleStepChange(-1, ...(args as [any, any, any, any, any, any]));
    }
  };

  const handleWheel = (e: React.WheelEvent, ...args: any[]) => {
    if (document.activeElement === e.target) {
      const delta = e.deltaY < 0 ? 1 : -1;
      handleStepChange(delta, ...(args as [any, any, any, any, any, any]));
    }
  };

  const handleUnitSelect = (attrId: number, newUnit: EAttributeUnit) => {
    if (onAttributeUnitChange) {
      onAttributeUnitChange(attrId, newUnit);
    }
  };

  const handleTextFieldToggle = (attrId: number) => {
    setTextFieldVisible((prev) => ({ ...prev, [attrId]: true }));
    onToggleInputField(attrId);
  };
  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (draggedId !== null && draggedId !== id) setDropTargetId(id);
  };
  const handleDragLeave = () => setDropTargetId(null);
  const handleDragEnd = () => {
    setDraggedId(null);
    setDropTargetId(null);
  };
  const handleDrop = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (draggedId === null || dropTargetId !== id) {
      handleDragEnd();
      return;
    }
    const reordered = (() => {
      const without = attributes.filter((a) => a.id !== draggedId);
      const dragged = attributes.find((a) => a.id === draggedId)!;
      if (id === -1) without.push(dragged);
      else {
        const idx = without.findIndex((a) => a.id === id);
        without.splice(idx, 0, dragged);
      }
      return without.map((a, i) => {
        const pred = i === 0 ? null : without[i - 1].id;
        const val = attributeValues[a.id] ?? a.value ?? '';
        return new AttributeModel(
          a.id,
          a.key,
          val,
          pred,
          a.type,
          a.unit,
          a.hidden,
          a.forced,
          a.ownerId,
          a.userId,
          a.publicFlag,
          a.localization,
          a.defaults,
          a.validation,
          a.unitFamily,
        );
      });
    })();
    onReorderAttributes(reordered);
    handleDragEnd();
  };

  const visibleAttributes = useMemo(() => attributes.filter((a) => !a.hidden), [attributes]);
  if (visibleAttributes.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {intl.formatMessage({ id: 'AttributeList.attributes', defaultMessage: 'Attributes' })}
      </Typography>

      {visibleAttributes.map((attr) => {
        const isDragged = draggedId === attr.id;
        const isOver = dropTargetId === attr.id;

        let storedVal = attributeValues[attr.id];
        const defaultObj = attr.defaults?.[defaultsRegionKey] || attr.defaults?.GLOBAL;

        if ((storedVal === undefined || storedVal === null) && attr.value !== undefined && attr.value !== null) {
            storedVal = String(attr.value);
        }

        let effectiveVal = storedVal;

        if (storedVal === undefined || storedVal === null || storedVal === '') {
          if (attr.type === EAttributeType.BOOL) {
            effectiveVal = defaultObj?.value !== undefined ? String(defaultObj.value) : 'true';
          } else if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
            effectiveVal = String(defaultObj.value);
          } else if (attr.type === EAttributeType.SELECTION) {
            const vals = getSelectionOptions((attr.validation?.[defaultsRegionKey] as ISelectionValidation)?.values as SelectionValue[] | undefined);
            const defaultValue = getDefaultSelectionValue(defaultObj?.value, vals);
          
            if (defaultValue !== null) {
              effectiveVal = serializeSelectionValue(defaultValue);
            }
          }
        }

        const rawVal = effectiveVal ?? '';
        const isChecked = rawVal === 'true';
        const showText = textFieldVisible[attr.id] || rawVal !== '';
        const isForced = attr.forced;
        const displayName = getLocalizedLabel(attr);
        const error = errors[attr.id];

        const validationObj = attr.validation?.[defaultsRegionKey] || attr.validation?.GLOBAL || {};
        const numValidation = validationObj as INumberValidation;
        const textValidation = validationObj as ITextValidation;
        const selValidation = validationObj as ISelectionValidation;

        const isStorageMetric = !isRegionUS;
        const isDisplayMetric = unitType === EUnitType.METRIC;
        const isNativeMode = isStorageMetric === isDisplayMetric;

        const storedUnit = attr.unit;
        let activeDisplayUnitKey = storedUnit as EAttributeUnit;

        if (!isNativeMode && storedUnit) {
          activeDisplayUnitKey = getCorrespondingUnit(storedUnit as EAttributeUnit, !isDisplayMetric);
        }

        const displayUnitLabel = attributeUnitDisplayMap[activeDisplayUnitKey] || activeDisplayUnitKey;

        const precision = numValidation.precision ?? 3;
        let calculatedDisplayValue = rawVal;

        if (!isNativeMode && attr.type === EAttributeType.NUMBER && rawVal && !isNaN(Number(rawVal)) && activeDisplayUnitKey && storedUnit) {
          let converted = convertAttributeValue(Number(rawVal), storedUnit as EAttributeUnit, !isDisplayMetric);

          if (!isImperialDisplay && IMPERIAL_TO_METRIC[storedUnit as EAttributeUnit]) {
            const scale = METRIC_SCALE_FACTORS[activeDisplayUnitKey] || 1;
            converted = converted / scale;
          }

          const roundFactor = Math.pow(10, precision);
          calculatedDisplayValue = String(Math.round(converted * roundFactor) / roundFactor);
        }

        const isEditing = editingId === attr.id;
        let valueToShow = calculatedDisplayValue;
        if (!isEditing && (valueToShow === undefined || valueToShow === null || valueToShow === '')) {
          if (defaultObj?.value) valueToShow = String(defaultObj.value);
        }

        if (!isEditing && attr.id === DefaultAttributeId.WALL_TYPE) {
          if (valueToShow === 'Standardwand') {
            valueToShow = intl.formatMessage({ id: 'WallType.standard', defaultMessage: 'Standard Wall' });
          } else if (valueToShow === 'Zwischenwand') {
            valueToShow = intl.formatMessage({ id: 'WallType.partition', defaultMessage: 'Partition Wall' });
          }
        }

        if (isEditing) {
          valueToShow = localValue;
        }

        let availableUnits: EAttributeUnit[] = [];
        const isNoneFamily = !attr.unitFamily || attr.unitFamily === EAttributeUnitFamily.NONE;

        const isUnitLocked = !!(numValidation.unit || defaultObj?.unit);

        if (isNativeMode && attr.type === EAttributeType.NUMBER && !isUnitLocked) {
          const familyToUse = isNoneFamily ? EAttributeUnitFamily.NONE : attr.unitFamily!;
          const rawUnits = unitFamilyToUnitRecord[familyToUse] || [];
          if (unitType === EUnitType.METRIC) {
            availableUnits = rawUnits.filter((u) => Object.prototype.hasOwnProperty.call(METRIC_TO_IMPERIAL, u));
          } else {
            availableUnits = rawUnits.filter((u) => Object.prototype.hasOwnProperty.call(IMPERIAL_TO_METRIC, u));
          }
          if (availableUnits.length > 0 && (isNoneFamily || !activeDisplayUnitKey)) {
            availableUnits.unshift('' as EAttributeUnit);
          }
        }

        let dropdownValue = activeDisplayUnitKey || '';
        if (availableUnits.length > 0 && activeDisplayUnitKey && !availableUnits.includes(activeDisplayUnitKey)) {
          dropdownValue = availableUnits.includes(activeDisplayUnitKey) ? activeDisplayUnitKey : availableUnits[0];
        }

        return (
          <Box
            key={attr.id}
            onDragOver={(e) => handleDragOver(e, attr.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, attr.id)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              py: 0,
              backgroundColor: isDragged ? 'rgba(0,0,0,0.05)' : 'transparent',
              opacity: isDragged ? 0.6 : 1,
              transform: isDragged ? 'scale(1.02)' : 'none',
              transition: 'transform 0.2s, opacity 0.2s',
              borderTop: isOver ? '2px dashed rgb(175,175,175)' : undefined,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: '30px', width: '100%' }}>
              {showDragHandle && (
                <IconButton
                  draggable
                  disableRipple
                  size="small"
                  onDragStart={(e) => handleDragStart(e, attr.id)}
                  onDragEnd={handleDragEnd}
                  sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' }, color: '#464646', p: 0.5, opacity: 1, background: 'none' }}
                >
                  <DragIndicator />
                </IconButton>
              )}

              <Typography
                variant="body2"
                sx={{
                  flex: '0 0 120px',
                  width: 120,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.2,
                  userSelect: isForced ? 'none' : undefined,
                  fontSize: '0.9rem',
                }}
              >
                {`${displayName}${valueToShow ? ':' : ''}`}
              </Typography>

              <Box sx={{ flex: 1 }}>
                {attr.type === EAttributeType.BOOL ? (
                  isForced ? (
                    <Typography variant="body2" sx={{ flex: 1, color: '#464646', fontSize: '0.9rem' }}>
                      {isChecked ? intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' }) : intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' })}
                    </Typography>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={isChecked}
                        onChange={(_, checked) => onAttributeValueChange(attr.id, checked ? 'true' : 'false')}
                        size="small"
                        sx={{
                          width: 36,
                          height: 32,
                          top: 6,
                          padding: 0,
                          '& .MuiSwitch-switchBase': { padding: 0, top: 2, left: 2, '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#464646', opacity: 1 } },
                          '& .MuiSwitch-thumb': { width: 16, height: 16, backgroundColor: '#fff' },
                          '& .MuiSwitch-track': { borderRadius: 10, height: 20, backgroundColor: '#464646', opacity: 1 },
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                        {isChecked ? intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' }) : intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' })}
                      </Typography>
                    </Box>
                  )
                ) : attr.type === EAttributeType.NUMBER ? (
                  isForced ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#464646', fontSize: '0.9rem' }}>
                        {valueToShow}
                      </Typography>
                      {displayUnitLabel && (
                        <Typography variant="body2" sx={{ color: '#464646', fontSize: '0.9rem' }}>
                          {displayUnitLabel}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <TextField
                      variant="standard"
                      size="small"
                      type="text"
                      inputMode="decimal"
                      inputRef={(el) => (inputRefs.current[attr.id] = el)}
                      error={!!error}
                      value={valueToShow}
                      onWheel={(e) => handleWheel(e, attr, valueToShow, activeDisplayUnitKey, storedUnit as EAttributeUnit, isNativeMode, numValidation)}
                      onFocus={() => handleInputFocus(attr.id, calculatedDisplayValue)}
                      onBlur={() => handleInputBlur(attr, activeDisplayUnitKey, storedUnit as EAttributeUnit, isNativeMode, numValidation)}
                      onChange={(e) => handleNumberChange(attr, e.target.value, activeDisplayUnitKey, storedUnit as EAttributeUnit, isNativeMode, numValidation)}
                      onKeyDown={(e) => handleKeyDown(e, attr, valueToShow, activeDisplayUnitKey, storedUnit as EAttributeUnit, isNativeMode, numValidation)}
                      placeholder={intl.formatMessage({ id: 'AttributeList.attributeNumberValuePlaceholder', defaultMessage: 'Enter number value' })}
                      InputProps={{
                        endAdornment:
                          isNativeMode && availableUnits.length > 0 && onAttributeUnitChange ? (
                            <InputAdornment position="end">
                              <Select
                                variant="standard"
                                disableUnderline
                                value={dropdownValue}
                                onChange={(e) => handleUnitSelect(attr.id, e.target.value as EAttributeUnit)}
                                sx={{
                                  '& .MuiSelect-select': {
                                    color: '#464646',
                                    fontSize: '0.9rem',
                                    paddingRight: '24px !important',
                                    textAlign: 'right',
                                    '&:focus': { backgroundColor: 'transparent' },
                                  },
                                  '& .MuiSvgIcon-root': { right: 0 },
                                }}
                              >
                                {availableUnits.map((u) => (
                                  <MenuItem key={u} value={u}>
                                    {u === '' ? intl.formatMessage({ id: 'AttributeList.none',defaultMessage: 'None' }) : attributeUnitDisplayMap[u as EAttributeUnit] || u}
                                  </MenuItem>
                                ))}
                              </Select>
                            </InputAdornment>
                          ) : (
                            displayUnitLabel && (
                              <InputAdornment position="end">
                                <Typography sx={{ color: '#464646', fontSize: '0.9rem' }}>{displayUnitLabel}</Typography>
                              </InputAdornment>
                            )
                          ),
                      }}
                      sx={{
                        flex: 1,
                        width: '100%',
                        '& .MuiInputBase-input': { py: 0.5, color: 'inherit', fontSize: '0.9rem', fontWeight: 400 },
                        '& .MuiInput-underline:before': { borderBottom: '1px solid #BABABA' },
                        '& .MuiInput-underline:hover:before': { borderBottom: '1px solid #464646' },
                        '& .MuiInput-underline:after': { borderBottom: '1px solid #464646' },
                      }}
                    />
                  )
                  ) : attr.type === EAttributeType.SELECTION ? (
                    isForced ? (
                      <Typography variant="body2" sx={{ flex: 1, color: '#464646', fontSize: '0.9rem' }}>
                        {getSelectionDisplayText(rawVal, intl.locale)}
                      </Typography>
                    ) : (
                      (() => {
                        const options = getSelectionOptions(selValidation.values as SelectionValue[] | undefined);
                        const selectValue = getSelectionValueForSelect(rawVal, options);
                  
                        return (
                          <Select
                            value={selectValue}
                            onChange={(e) => onAttributeValueChange(attr.id, coerceSelectionValueForStorage(e.target.value))}
                            variant="standard"
                            displayEmpty
                            sx={{ flex: 1, width: '100%', fontSize: '0.9rem', '&:before': { borderBottom: '1px solid #BABABA' } }}
                          >
                            <MenuItem value="" disabled>
                              <Typography variant="caption" color="textSecondary">
                                {intl.formatMessage({ id: 'AttributeList.select', defaultMessage: 'Select...' })}
                              </Typography>
                            </MenuItem>
                  
                            {options.map((opt) => {
                              const optionValue = serializeSelectionValue(opt);
                  
                              return (
                                <MenuItem key={optionValue} value={optionValue}>
                                  {getLocalizedSelectionLabel(opt, intl.locale)}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        );
                      })()
                    )
                ) : showText ? (
                  isForced ? (
                    <Typography variant="body2" sx={{ flex: 1, color: '#464646', fontSize: '0.9rem' }}>
                      {valueToShow}
                    </Typography>
                  ) : (
                    <TextField
                      variant="standard"
                      size="small"
                      type="text"
                      value={valueToShow}
                      onFocus={() => handleInputFocus(attr.id, rawVal)}
                      onBlur={() => handleInputBlur(attr, activeDisplayUnitKey, storedUnit as EAttributeUnit, isNativeMode, {} as any)}
                      onChange={(e) => handleTextChange(attr.id, e.target.value, textValidation)}
                      placeholder={intl.formatMessage({ id: 'AttributeList.attributeValuePlaceholder' })}
                      inputProps={{ maxLength: textValidation.maxLength ?? 150 }}
                      sx={{
                        flex: 1,
                        width: '100%',
                        '& .MuiInputBase-input': { py: 0.5, fontSize: '0.9rem', fontWeight: 400 },
                        '& .MuiInput-underline:before': { borderBottom: '1px solid #BABABA' },
                        '& .MuiInput-underline:hover:before': { borderBottom: '1px solid #464646' },
                        '& .MuiInput-underline:after': { borderBottom: '1px solid #464646' },
                      }}
                    />
                  )
                ) : (
                  <Box flex={1} />
                )}
              </Box>

              <Box display="flex" gap={0.5} ml={1}>
                {attr.type === EAttributeType.TEXT && !showText && !isForced && (
                  <IconButton size="small" sx={{ color: '#464646' }} onClick={() => handleTextFieldToggle(attr.id)}>
                    <TextFieldsIcon />
                  </IconButton>
                )}
                {!isForced && (
                  <Tooltip title={intl.formatMessage({ id: 'AttributeList.deleteAttribut' })}>
                    <IconButton size="small" sx={{ color: '#464646' }} onClick={() => onDeleteAttribute(attr.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {error && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', paddingRight: '40px' }}>
                <FormHelperText error sx={{ mt: 0, fontSize: '0.75rem', textAlign: 'center' }}>
                  {error}
                </FormHelperText>
              </Box>
            )}
          </Box>
        );
      })}
      <Box
        onDragOver={(e) => handleDragOver(e, -1)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, -1)}
        sx={{ height: 10, borderRadius: 1, borderTop: dropTargetId === -1 ? '2px dashed rgb(175,175,175)' : 'none' }}
      />
    </Box>
  );
};

export default AttributeList;